import { useState, useEffect, useMemo, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Layout } from '@/components/Layout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { Plus, Bell, Search, Filter, X, Calendar as CalendarIcon, List, Clock, Check, PlayCircle, Edit, Copy, Trash2, MoreVertical, AlertTriangle, User, Link2, RefreshCw, ChevronLeft, ChevronRight } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const PRIORIDADES = [
  { value: 'baixa', label: 'Baixa', cor: 'bg-slate-500' },
  { value: 'media', label: 'Média', cor: 'bg-blue-500' },
  { value: 'alta', label: 'Alta', cor: 'bg-orange-500' },
  { value: 'urgente', label: 'Urgente', cor: 'bg-red-500' },
];
const STATUS = [
  { value: 'pendente', label: 'Pendente', cor: 'bg-yellow-500/20 text-yellow-600' },
  { value: 'em_andamento', label: 'Em andamento', cor: 'bg-blue-500/20 text-blue-600' },
  { value: 'concluido', label: 'Concluído', cor: 'bg-green-500/20 text-green-600' },
  { value: 'atrasado', label: 'Atrasado', cor: 'bg-red-500/20 text-red-600' },
  { value: 'adiado', label: 'Adiado', cor: 'bg-purple-500/20 text-purple-600' },
  { value: 'cancelado', label: 'Cancelado', cor: 'bg-slate-500/20 text-slate-600' },
];
const REPETICOES = [
  { value: 'nao', label: 'Não repetir' },
  { value: 'diaria', label: 'Todos os dias' },
  { value: 'dias_uteis', label: 'Dias úteis' },
  { value: 'semanal', label: 'Toda semana' },
  { value: 'quinzenal', label: 'A cada 2 semanas' },
  { value: 'mensal', label: 'Todo mês' },
  { value: 'trimestral', label: 'A cada 3 meses' },
  { value: 'semestral', label: 'A cada 6 meses' },
  { value: 'anual', label: 'Todo ano' },
  { value: 'personalizada', label: 'Personalizada' },
];
const VINCULOS = [
  { value: 'nenhum', label: 'Sem vínculo' },
  { value: 'cliente', label: 'Cliente' },
  { value: 'pedido', label: 'Pedido' },
  { value: 'conta_pagar', label: 'Conta a Pagar' },
  { value: 'conta_receber', label: 'Conta a Receber' },
  { value: 'entrega', label: 'Entrega' },
];
const NOTIF_OPTIONS = [
  { min: 0, label: 'No momento' },
  { min: 15, label: '15 min antes' },
  { min: 30, label: '30 min antes' },
  { min: 60, label: '1 hora antes' },
  { min: 180, label: '3 horas antes' },
  { min: 1440, label: '1 dia antes' },
  { min: 4320, label: '3 dias antes' },
  { min: 10080, label: '7 dias antes' },
];

const formatDate = (str) => {
  if (!str) return '-';
  const [y, m, d] = String(str).split('T')[0].split('-');
  return `${d}/${m}/${y}`;
};

export default function Lembretes({ prefiltroVinculo = null }) {
  const [reminders, setReminders] = useState([]);
  const [categorias, setCategorias] = useState([]);
  const [stats, setStats] = useState({ hoje: 0, proximos: 0, atrasados: 0, concluidos_mes: 0, urgentes: 0 });
  const [loading, setLoading] = useState(false);
  const [view, setView] = useState('lista'); // 'lista' | 'calendario'

  // Filtros
  const [preset, setPreset] = useState('');
  const [q, setQ] = useState('');
  const [fCategoria, setFCategoria] = useState('');
  const [fPrioridade, setFPrioridade] = useState('');
  const [fStatus, setFStatus] = useState('');
  const [showFilters, setShowFilters] = useState(false);

  // Modal
  const [showFormModal, setShowFormModal] = useState(false);
  const [editingReminder, setEditingReminder] = useState(null);
  const [saving, setSaving] = useState(false);

  // Detalhes
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [detailReminder, setDetailReminder] = useState(null);
  const [detailHistory, setDetailHistory] = useState([]);

  // Adiar
  const [showAdiarModal, setShowAdiarModal] = useState(false);
  const [adiarTarget, setAdiarTarget] = useState(null);
  const [adiarDate, setAdiarDate] = useState('');
  const [adiarHora, setAdiarHora] = useState('');

  // Confirmação de exclusão
  const [deleteTarget, setDeleteTarget] = useState(null);

  // Calendário
  const [calMonth, setCalMonth] = useState(new Date());

  // Vinculo — buscar registros
  const [vinculoSearch, setVinculoSearch] = useState('');
  const [vinculoResults, setVinculoResults] = useState([]);

  const fetchStats = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/api/reminders/stats`);
      setStats(res.data);
    } catch (e) {
      // ignore
    }
  }, []);

  const buildQuery = useCallback(() => {
    const params = new URLSearchParams();
    if (preset) params.set('preset', preset);
    if (q) params.set('q', q);
    if (fCategoria) params.set('categoria_id', fCategoria);
    if (fPrioridade) params.set('prioridade', fPrioridade);
    if (fStatus) params.set('status', fStatus);
    if (prefiltroVinculo?.tipo) params.set('vinculo_tipo', prefiltroVinculo.tipo);
    if (prefiltroVinculo?.id) params.set('vinculo_id', prefiltroVinculo.id);
    return params.toString();
  }, [preset, q, fCategoria, fPrioridade, fStatus, prefiltroVinculo]);

  const fetchReminders = useCallback(async () => {
    setLoading(true);
    try {
      const qs = buildQuery();
      const res = await axios.get(`${API}/api/reminders${qs ? '?' + qs : ''}`);
      setReminders(res.data || []);
    } catch (e) {
      toast.error('Erro ao carregar lembretes');
    } finally {
      setLoading(false);
    }
  }, [buildQuery]);

  const fetchCategorias = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/api/reminder-categories`);
      let cats = res.data || [];
      if (cats.length === 0) {
        await axios.post(`${API}/api/reminders/seed-defaults`);
        const res2 = await axios.get(`${API}/api/reminder-categories`);
        cats = res2.data || [];
      }
      setCategorias(cats);
    } catch (e) {
      // ignore
    }
  }, []);

  useEffect(() => { fetchCategorias(); fetchStats(); }, [fetchCategorias, fetchStats]);
  useEffect(() => { fetchReminders(); }, [fetchReminders]);

  const clearFilters = () => {
    setPreset(''); setQ(''); setFCategoria(''); setFPrioridade(''); setFStatus('');
  };

  const openNovo = () => {
    setEditingReminder(null);
    setShowFormModal(true);
  };
  const openEditar = (r) => {
    setEditingReminder(r);
    setShowFormModal(true);
  };

  const abrirDetalhes = async (r) => {
    setDetailReminder(r);
    setShowDetailModal(true);
    try {
      const res = await axios.get(`${API}/api/reminders/${r.id}/history`);
      setDetailHistory(res.data || []);
    } catch (e) {
      setDetailHistory([]);
    }
  };

  const handleConcluir = async (r, ev) => {
    if (ev) ev.stopPropagation();
    try {
      await axios.post(`${API}/api/reminders/${r.id}/concluir`);
      toast.success('Lembrete concluído!');
      fetchReminders(); fetchStats();
    } catch (e) { toast.error('Erro ao concluir'); }
  };
  const handleIniciar = async (r, ev) => {
    if (ev) ev.stopPropagation();
    try {
      await axios.post(`${API}/api/reminders/${r.id}/iniciar`);
      toast.success('Iniciado!');
      fetchReminders(); fetchStats();
    } catch (e) { toast.error('Erro ao iniciar'); }
  };
  const handleDuplicar = async (r, ev) => {
    if (ev) ev.stopPropagation();
    try {
      await axios.post(`${API}/api/reminders/${r.id}/duplicar`);
      toast.success('Duplicado!');
      fetchReminders(); fetchStats();
    } catch (e) { toast.error('Erro ao duplicar'); }
  };
  const confirmarDelete = async () => {
    if (!deleteTarget) return;
    try {
      await axios.delete(`${API}/api/reminders/${deleteTarget.id}`);
      toast.success('Excluído!');
      setDeleteTarget(null);
      fetchReminders(); fetchStats();
    } catch (e) { toast.error('Erro ao excluir'); }
  };
  const handleAdiar = (r, tipo) => {
    setAdiarTarget(r);
    const hoje = new Date();
    let d = new Date(hoje);
    if (tipo === 'mais_tarde') { d.setHours(d.getHours() + 3); setAdiarDate(d.toISOString().split('T')[0]); setAdiarHora(String(d.getHours()).padStart(2, '0') + ':' + String(d.getMinutes()).padStart(2, '0')); enviarAdiar(r, d.toISOString().split('T')[0], String(d.getHours()).padStart(2, '0') + ':' + String(d.getMinutes()).padStart(2, '0')); return; }
    if (tipo === 'amanha') { d.setDate(d.getDate() + 1); enviarAdiar(r, d.toISOString().split('T')[0]); return; }
    if (tipo === 'proxima_semana') { d.setDate(d.getDate() + 7); enviarAdiar(r, d.toISOString().split('T')[0]); return; }
    // custom
    setAdiarDate(''); setAdiarHora('');
    setShowAdiarModal(true);
  };
  const enviarAdiar = async (r, novaData, novoHorario) => {
    try {
      await axios.post(`${API}/api/reminders/${r.id}/adiar`, { nova_data: novaData, novo_horario: novoHorario });
      toast.success('Adiado!');
      setShowAdiarModal(false); setAdiarTarget(null);
      fetchReminders(); fetchStats();
    } catch (e) { toast.error('Erro ao adiar'); }
  };

  const categoriasById = useMemo(() => {
    const m = {};
    categorias.forEach(c => { m[c.id] = c; });
    return m;
  }, [categorias]);

  // Card indicadores
  const renderIndicador = (label, value, cor, presetKey) => (
    <button
      key={presetKey}
      onClick={() => setPreset(preset === presetKey ? '' : presetKey)}
      className={`border rounded-lg p-3 text-left transition ${preset === presetKey ? 'ring-2 ring-primary' : ''} ${cor}`}
      data-testid={`ind-${presetKey}`}
    >
      <p className="text-xs opacity-75">{label}</p>
      <p className="text-2xl font-bold">{value}</p>
    </button>
  );

  return (
    <Layout>
    <div className="p-4 space-y-4 pb-24" data-testid="lembretes-page">
      {/* Header */}
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Bell className="h-6 w-6 text-primary" />
            Lembretes
          </h1>
          {prefiltroVinculo && (
            <p className="text-sm text-muted-foreground">Filtrado por: {prefiltroVinculo.label}</p>
          )}
        </div>
        <div className="flex items-center gap-2">
          <div className="flex rounded-lg border overflow-hidden">
            <button className={`px-3 py-1 ${view === 'lista' ? 'bg-primary text-primary-foreground' : ''}`} onClick={() => setView('lista')} data-testid="view-lista"><List className="h-4 w-4" /></button>
            <button className={`px-3 py-1 ${view === 'calendario' ? 'bg-primary text-primary-foreground' : ''}`} onClick={() => setView('calendario')} data-testid="view-calendario"><CalendarIcon className="h-4 w-4" /></button>
          </div>
          <Button onClick={openNovo} data-testid="btn-novo-lembrete">
            <Plus className="h-4 w-4 mr-1" /> Novo
          </Button>
        </div>
      </div>

      {/* Indicadores */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
        {renderIndicador('Hoje', stats.hoje, 'bg-blue-500/10 hover:bg-blue-500/20', 'hoje')}
        {renderIndicador('Próximos (7d)', stats.proximos, 'bg-purple-500/10 hover:bg-purple-500/20', 'semana')}
        {renderIndicador('Atrasados', stats.atrasados, 'bg-red-500/10 hover:bg-red-500/20', 'atrasados')}
        {renderIndicador('Concluídos no mês', stats.concluidos_mes, 'bg-green-500/10 hover:bg-green-500/20', 'mes_concluidos')}
      </div>

      {/* Busca + filtros */}
      <Card>
        <CardContent className="pt-4 space-y-3">
          <div className="flex gap-2">
            <div className="relative flex-1">
              <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <Input placeholder="Buscar título, descrição ou vinculado..." value={q} onChange={(e) => setQ(e.target.value)} className="pl-9" data-testid="search-lembretes" />
            </div>
            <Button variant="outline" size="icon" onClick={() => setShowFilters(!showFilters)} data-testid="btn-toggle-filters"><Filter className="h-4 w-4" /></Button>
            {(preset || q || fCategoria || fPrioridade || fStatus) && (
              <Button variant="ghost" size="sm" onClick={clearFilters} data-testid="btn-clear-filters"><X className="h-4 w-4 mr-1" />Limpar</Button>
            )}
          </div>
          {showFilters && (
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
              <Select value={fCategoria} onValueChange={setFCategoria}>
                <SelectTrigger><SelectValue placeholder="Categoria" /></SelectTrigger>
                <SelectContent>
                  {categorias.map(c => <SelectItem key={c.id} value={c.id}>{c.nome}</SelectItem>)}
                </SelectContent>
              </Select>
              <Select value={fPrioridade} onValueChange={setFPrioridade}>
                <SelectTrigger><SelectValue placeholder="Prioridade" /></SelectTrigger>
                <SelectContent>
                  {PRIORIDADES.map(p => <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>)}
                </SelectContent>
              </Select>
              <Select value={fStatus} onValueChange={setFStatus}>
                <SelectTrigger><SelectValue placeholder="Status" /></SelectTrigger>
                <SelectContent>
                  {STATUS.map(s => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
                </SelectContent>
              </Select>
              <Select value={preset} onValueChange={setPreset}>
                <SelectTrigger><SelectValue placeholder="Atalhos" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="hoje">Hoje</SelectItem>
                  <SelectItem value="amanha">Amanhã</SelectItem>
                  <SelectItem value="semana">Esta semana</SelectItem>
                  <SelectItem value="atrasados">Atrasados</SelectItem>
                  <SelectItem value="urgentes">Urgentes</SelectItem>
                  <SelectItem value="meus">Meus lembretes</SelectItem>
                  <SelectItem value="sem_data">Sem data</SelectItem>
                  <SelectItem value="concluidos">Concluídos</SelectItem>
                </SelectContent>
              </Select>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Lista ou Calendário */}
      {view === 'lista' ? (
        <ListaLembretes
          reminders={reminders}
          loading={loading}
          categoriasById={categoriasById}
          onEdit={openEditar}
          onConcluir={handleConcluir}
          onIniciar={handleIniciar}
          onAdiar={handleAdiar}
          onDuplicar={handleDuplicar}
          onDelete={(r) => setDeleteTarget(r)}
          onDetail={abrirDetalhes}
        />
      ) : (
        <CalendarioLembretes
          reminders={reminders}
          categoriasById={categoriasById}
          month={calMonth}
          setMonth={setCalMonth}
          onDetail={abrirDetalhes}
        />
      )}

      {/* Modal Novo/Editar */}
      {showFormModal && (
        <ReminderFormModal
          open={showFormModal}
          onClose={() => setShowFormModal(false)}
          editing={editingReminder}
          categorias={categorias}
          prefiltroVinculo={prefiltroVinculo}
          onSaved={() => { setShowFormModal(false); fetchReminders(); fetchStats(); }}
        />
      )}

      {/* Modal Detalhes */}
      <Dialog open={showDetailModal} onOpenChange={setShowDetailModal}>
        <DialogContent className="max-w-lg max-h-[90vh] p-0 gap-0 flex flex-col overflow-hidden">
          <DialogHeader className="px-4 py-3 border-b bg-muted/30">
            <DialogTitle>{detailReminder?.titulo}</DialogTitle>
            <DialogDescription className="text-xs">
              Criado por {detailReminder?.createdByNome || '-'} em {detailReminder?.createdAt ? new Date(detailReminder.createdAt).toLocaleString('pt-BR') : '-'}
            </DialogDescription>
          </DialogHeader>
          <div className="flex-1 overflow-y-auto p-4 space-y-3 text-sm">
            {detailReminder?.descricao && <div><span className="text-muted-foreground">Descrição: </span>{detailReminder.descricao}</div>}
            <div className="grid grid-cols-2 gap-2">
              <div><span className="text-muted-foreground">Data: </span>{formatDate(detailReminder?.data)} {detailReminder?.horario ? `às ${detailReminder.horario}` : ''}</div>
              <div><span className="text-muted-foreground">Data limite: </span>{formatDate(detailReminder?.data_limite)}</div>
              <div><span className="text-muted-foreground">Prioridade: </span>{PRIORIDADES.find(p => p.value === detailReminder?.prioridade)?.label}</div>
              <div><span className="text-muted-foreground">Status: </span>{STATUS.find(s => s.value === detailReminder?.status)?.label}</div>
            </div>
            {detailReminder?.vinculo?.tipo && detailReminder.vinculo.tipo !== 'nenhum' && (
              <div className="p-2 bg-muted/40 rounded flex items-center gap-2">
                <Link2 className="h-4 w-4" />
                <span className="font-medium">{VINCULOS.find(v => v.value === detailReminder.vinculo.tipo)?.label}:</span>
                <span>{detailReminder.vinculo.label}</span>
              </div>
            )}
            <div>
              <h4 className="font-semibold text-xs uppercase text-muted-foreground mb-2">Histórico</h4>
              <div className="space-y-1 max-h-48 overflow-y-auto">
                {detailHistory.length === 0 && <p className="text-xs text-muted-foreground">Nenhum registro.</p>}
                {detailHistory.map(h => (
                  <div key={h.id} className="text-xs border-l-2 border-primary/40 pl-2 py-1">
                    <span className="font-semibold">{h.acao}</span> — {h.user_nome} em {new Date(h.createdAt).toLocaleString('pt-BR')}
                  </div>
                ))}
              </div>
            </div>
            <div className="flex gap-2 pt-2 border-t">
              <Button variant="outline" size="sm" onClick={() => { setShowDetailModal(false); openEditar(detailReminder); }}><Edit className="h-4 w-4 mr-1" />Editar</Button>
              {detailReminder?.status !== 'concluido' && (
                <Button size="sm" onClick={() => { handleConcluir(detailReminder); setShowDetailModal(false); }}><Check className="h-4 w-4 mr-1" />Concluir</Button>
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Modal Adiar personalizado */}
      <Dialog open={showAdiarModal} onOpenChange={setShowAdiarModal}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle>Adiar para</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs">Nova data</Label>
              <Input type="date" value={adiarDate} onChange={(e) => setAdiarDate(e.target.value)} data-testid="adiar-nova-data" />
            </div>
            <div>
              <Label className="text-xs">Novo horário (opcional)</Label>
              <Input type="time" value={adiarHora} onChange={(e) => setAdiarHora(e.target.value)} />
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setShowAdiarModal(false)}>Cancelar</Button>
              <Button onClick={() => enviarAdiar(adiarTarget, adiarDate, adiarHora)} disabled={!adiarDate}>Adiar</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Confirmação de exclusão */}
      <AlertDialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Excluir lembrete?</AlertDialogTitle>
            <AlertDialogDescription>
              Esta ação não pode ser desfeita. &quot;{deleteTarget?.titulo}&quot; será removido.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={confirmarDelete} data-testid="confirm-delete-lembrete">Excluir</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
    </Layout>
  );
}

// ============ Lista de lembretes ============
function ListaLembretes({ reminders, loading, categoriasById, onEdit, onConcluir, onIniciar, onAdiar, onDuplicar, onDelete, onDetail }) {
  if (loading) return <div className="text-center py-8 text-muted-foreground">Carregando...</div>;
  if (reminders.length === 0) return <div className="text-center py-8 text-muted-foreground">Nenhum lembrete encontrado.</div>;
  return (
    <div className="space-y-2">
      {reminders.map(r => (
        <ReminderCard key={r.id} reminder={r} categoria={categoriasById[r.categoria_id]}
          onEdit={onEdit} onConcluir={onConcluir} onIniciar={onIniciar} onAdiar={onAdiar} onDuplicar={onDuplicar} onDelete={onDelete} onDetail={onDetail} />
      ))}
    </div>
  );
}

function ReminderCard({ reminder: r, categoria, onEdit, onConcluir, onIniciar, onAdiar, onDuplicar, onDelete, onDetail }) {
  const [menuOpen, setMenuOpen] = useState(false);
  const [adiarOpen, setAdiarOpen] = useState(false);
  const prio = PRIORIDADES.find(p => p.value === r.prioridade) || PRIORIDADES[1];
  const st = STATUS.find(s => s.value === r.status) || STATUS[0];
  const isConcluido = r.status === 'concluido';
  const isCancelado = r.status === 'cancelado';
  const isAtrasado = r.status === 'atrasado';
  return (
    <Card className={`transition ${isAtrasado ? 'border-red-500/40' : ''} ${isConcluido ? 'opacity-70' : ''}`} data-testid={`reminder-card-${r.id}`}>
      <CardContent className="p-3">
        <div className="flex items-start gap-2">
          <div className={`w-1 self-stretch rounded ${prio.cor}`} />
          <div className="flex-1 min-w-0" onClick={() => onDetail(r)}>
            <div className="flex items-center gap-2 flex-wrap">
              <h3 className={`font-semibold text-sm ${isConcluido ? 'line-through' : ''}`}>{r.titulo}</h3>
              <Badge className={st.cor} variant="outline">{st.label}</Badge>
              {r.recorrencia?.tipo && r.recorrencia.tipo !== 'nao' && (
                <RefreshCw className="h-3 w-3 text-blue-500" />
              )}
              {isAtrasado && <AlertTriangle className="h-3 w-3 text-red-500" />}
            </div>
            <div className="flex items-center gap-2 text-xs text-muted-foreground mt-1 flex-wrap">
              {r.data && <span className="flex items-center gap-1"><CalendarIcon className="h-3 w-3" />{formatDate(r.data)} {r.horario ? `${r.horario}` : ''}</span>}
              {categoria && <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full" style={{ background: categoria.cor }} />{categoria.nome}</span>}
              {r.vinculo?.tipo && r.vinculo.tipo !== 'nenhum' && r.vinculo.label && (
                <span className="flex items-center gap-1"><Link2 className="h-3 w-3" />{r.vinculo.label}</span>
              )}
            </div>
          </div>
          <div className="flex items-center gap-1 shrink-0">
            {!isConcluido && !isCancelado && (
              <>
                <Button size="icon" variant="ghost" onClick={(e) => onConcluir(r, e)} title="Concluir" data-testid={`btn-concluir-${r.id}`}><Check className="h-4 w-4 text-green-600" /></Button>
                {r.status !== 'em_andamento' && (
                  <Button size="icon" variant="ghost" onClick={(e) => onIniciar(r, e)} title="Iniciar"><PlayCircle className="h-4 w-4 text-blue-500" /></Button>
                )}
              </>
            )}
            <div className="relative">
              <Button size="icon" variant="ghost" onClick={(e) => { e.stopPropagation(); setMenuOpen(!menuOpen); }} data-testid={`btn-menu-${r.id}`}><MoreVertical className="h-4 w-4" /></Button>
              {menuOpen && (
                <>
                  <div className="fixed inset-0 z-30" onClick={() => setMenuOpen(false)} />
                  <div className="absolute right-0 top-full mt-1 z-40 bg-background border rounded-lg shadow-lg py-1 min-w-[180px]">
                    <button className="w-full text-left px-3 py-2 text-sm hover:bg-muted flex items-center gap-2" onClick={(e) => { e.stopPropagation(); setMenuOpen(false); onEdit(r); }}><Edit className="h-4 w-4" />Editar</button>
                    <button className="w-full text-left px-3 py-2 text-sm hover:bg-muted flex items-center gap-2" onClick={(e) => { e.stopPropagation(); setMenuOpen(false); setAdiarOpen(true); }}><Clock className="h-4 w-4" />Adiar</button>
                    <button className="w-full text-left px-3 py-2 text-sm hover:bg-muted flex items-center gap-2" onClick={(e) => { e.stopPropagation(); setMenuOpen(false); onDuplicar(r, e); }}><Copy className="h-4 w-4" />Duplicar</button>
                    <button className="w-full text-left px-3 py-2 text-sm hover:bg-muted flex items-center gap-2 text-red-500" onClick={(e) => { e.stopPropagation(); setMenuOpen(false); onDelete(r); }}><Trash2 className="h-4 w-4" />Excluir</button>
                  </div>
                </>
              )}
              {adiarOpen && (
                <>
                  <div className="fixed inset-0 z-30" onClick={() => setAdiarOpen(false)} />
                  <div className="absolute right-0 top-full mt-1 z-40 bg-background border rounded-lg shadow-lg py-1 min-w-[180px]">
                    <button className="w-full text-left px-3 py-2 text-sm hover:bg-muted" onClick={(e) => { e.stopPropagation(); setAdiarOpen(false); onAdiar(r, 'mais_tarde'); }}>Mais tarde (3h)</button>
                    <button className="w-full text-left px-3 py-2 text-sm hover:bg-muted" onClick={(e) => { e.stopPropagation(); setAdiarOpen(false); onAdiar(r, 'amanha'); }}>Amanhã</button>
                    <button className="w-full text-left px-3 py-2 text-sm hover:bg-muted" onClick={(e) => { e.stopPropagation(); setAdiarOpen(false); onAdiar(r, 'proxima_semana'); }}>Próxima semana</button>
                    <button className="w-full text-left px-3 py-2 text-sm hover:bg-muted" onClick={(e) => { e.stopPropagation(); setAdiarOpen(false); onAdiar(r, 'custom'); }}>Escolher data...</button>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

// ============ Modal Formulário ============
function ReminderFormModal({ open, onClose, editing, categorias, prefiltroVinculo, onSaved }) {
  const [form, setForm] = useState({
    titulo: '', descricao: '', data: '', horario: '', data_limite: '',
    prioridade: 'media', categoria_id: '', status: 'pendente', observacoes: '',
    vinculo: prefiltroVinculo || { tipo: 'nenhum', id: null, label: null },
    recorrencia: { tipo: 'nao' },
    notificacoes: [],
  });
  const [saving, setSaving] = useState(false);
  const [vinculoBusca, setVinculoBusca] = useState('');
  const [vinculoResults, setVinculoResults] = useState([]);

  useEffect(() => {
    if (editing) {
      setForm({
        titulo: editing.titulo || '',
        descricao: editing.descricao || '',
        data: editing.data || '',
        horario: editing.horario || '',
        data_limite: editing.data_limite || '',
        prioridade: editing.prioridade || 'media',
        categoria_id: editing.categoria_id || '',
        status: editing.status || 'pendente',
        observacoes: editing.observacoes || '',
        vinculo: editing.vinculo || { tipo: 'nenhum' },
        recorrencia: editing.recorrencia || { tipo: 'nao' },
        notificacoes: editing.notificacoes || [],
      });
    } else if (prefiltroVinculo) {
      setForm(f => ({ ...f, vinculo: prefiltroVinculo }));
    }
  }, [editing, prefiltroVinculo]);

  const buscarVinculo = async () => {
    if (!vinculoBusca.trim()) return;
    const tipo = form.vinculo.tipo;
    try {
      let url = '';
      if (tipo === 'cliente') url = `${API}/api/customers?search=${encodeURIComponent(vinculoBusca)}&limit=8`;
      else if (tipo === 'pedido') url = `${API}/api/orders?search=${encodeURIComponent(vinculoBusca)}&limit=8`;
      else if (tipo === 'conta_pagar') url = `${API}/api/bills?search=${encodeURIComponent(vinculoBusca)}&limit=8`;
      else if (tipo === 'conta_receber') url = `${API}/api/bills-receivable?search=${encodeURIComponent(vinculoBusca)}&limit=8`;
      else if (tipo === 'entrega') url = `${API}/api/orders?search=${encodeURIComponent(vinculoBusca)}&limit=8`;
      if (!url) return;
      const res = await axios.get(url);
      const data = res.data || [];
      const list = Array.isArray(data) ? data : (data.customers || data.orders || data.bills || data.data || []);
      setVinculoResults(list.slice(0, 8));
    } catch (e) {
      setVinculoResults([]);
    }
  };

  const selecionarVinculado = (item) => {
    const tipo = form.vinculo.tipo;
    let label = '';
    if (tipo === 'cliente') label = item.nome || item.razao_social;
    else if (tipo === 'pedido' || tipo === 'entrega') label = `${item.numero_pedido || item.id} - ${item.cliente_nome || item.nome || ''}`;
    else if (tipo === 'conta_pagar' || tipo === 'conta_receber') label = `${item.descricao || ''} - R$ ${Number(item.valor || 0).toFixed(2)}`;
    setForm({ ...form, vinculo: { tipo, id: item.id, label } });
    setVinculoResults([]); setVinculoBusca('');
  };

  const toggleNotificacao = (min) => {
    const exists = form.notificacoes.find(n => n.tipo === 'antes' && n.minutos_antes === min);
    if (exists) {
      setForm({ ...form, notificacoes: form.notificacoes.filter(n => !(n.tipo === 'antes' && n.minutos_antes === min)) });
    } else {
      setForm({ ...form, notificacoes: [...form.notificacoes, { tipo: 'antes', minutos_antes: min }] });
    }
  };

  const salvar = async () => {
    if (!form.titulo.trim()) { toast.error('Título obrigatório'); return; }
    setSaving(true);
    try {
      const payload = { ...form };
      if (!payload.categoria_id) delete payload.categoria_id;
      if (editing) {
        await axios.put(`${API}/api/reminders/${editing.id}`, payload);
        toast.success('Lembrete atualizado');
      } else {
        await axios.post(`${API}/api/reminders`, payload);
        toast.success('Lembrete criado');
      }
      onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erro ao salvar');
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg max-h-[90vh] p-0 gap-0 flex flex-col overflow-hidden">
        <DialogHeader className="px-4 py-3 border-b bg-muted/30 shrink-0">
          <DialogTitle>{editing ? 'Editar Lembrete' : 'Novo Lembrete'}</DialogTitle>
        </DialogHeader>
        <div className="flex-1 overflow-y-auto p-4 space-y-3">
          <div>
            <Label className="text-xs">Título *</Label>
            <Input value={form.titulo} onChange={(e) => setForm({ ...form, titulo: e.target.value })} data-testid="form-titulo" />
          </div>
          <div>
            <Label className="text-xs">Descrição</Label>
            <textarea className="w-full border rounded-md px-3 py-2 text-sm bg-background min-h-[60px]" value={form.descricao} onChange={(e) => setForm({ ...form, descricao: e.target.value })} />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label className="text-xs">Data</Label>
              <Input type="date" value={form.data || ''} onChange={(e) => setForm({ ...form, data: e.target.value })} data-testid="form-data" />
            </div>
            <div>
              <Label className="text-xs">Horário</Label>
              <Input type="time" value={form.horario || ''} onChange={(e) => setForm({ ...form, horario: e.target.value })} />
            </div>
          </div>
          <div>
            <Label className="text-xs">Data limite</Label>
            <Input type="date" value={form.data_limite || ''} onChange={(e) => setForm({ ...form, data_limite: e.target.value })} />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label className="text-xs">Prioridade</Label>
              <Select value={form.prioridade} onValueChange={(v) => setForm({ ...form, prioridade: v })}>
                <SelectTrigger data-testid="form-prioridade"><SelectValue /></SelectTrigger>
                <SelectContent>{PRIORIDADES.map(p => <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Status</Label>
              <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{STATUS.map(s => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <Label className="text-xs">Categoria</Label>
            <Select value={form.categoria_id || ''} onValueChange={(v) => setForm({ ...form, categoria_id: v })}>
              <SelectTrigger data-testid="form-categoria"><SelectValue placeholder="Selecione..." /></SelectTrigger>
              <SelectContent>{categorias.map(c => <SelectItem key={c.id} value={c.id}>{c.nome}</SelectItem>)}</SelectContent>
            </Select>
          </div>

          <div>
            <Label className="text-xs">Vincular a</Label>
            <Select value={form.vinculo.tipo} onValueChange={(v) => setForm({ ...form, vinculo: { tipo: v, id: null, label: null } })}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>{VINCULOS.map(v => <SelectItem key={v.value} value={v.value}>{v.label}</SelectItem>)}</SelectContent>
            </Select>
            {form.vinculo.tipo !== 'nenhum' && !form.vinculo.id && (
              <div className="mt-2 space-y-1">
                <div className="flex gap-1">
                  <Input placeholder="Buscar..." value={vinculoBusca} onChange={(e) => setVinculoBusca(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), buscarVinculo())} />
                  <Button type="button" size="sm" onClick={buscarVinculo}><Search className="h-4 w-4" /></Button>
                </div>
                {vinculoResults.length > 0 && (
                  <div className="border rounded max-h-40 overflow-y-auto">
                    {vinculoResults.map((item, i) => (
                      <button key={i} type="button" className="w-full text-left px-2 py-1 hover:bg-muted text-sm" onClick={() => selecionarVinculado(item)}>
                        {form.vinculo.tipo === 'cliente' && (item.nome || item.razao_social)}
                        {(form.vinculo.tipo === 'pedido' || form.vinculo.tipo === 'entrega') && `${item.numero_pedido || item.id} - ${item.cliente_nome || item.nome || ''}`}
                        {(form.vinculo.tipo === 'conta_pagar' || form.vinculo.tipo === 'conta_receber') && `${item.descricao} - R$ ${Number(item.valor || 0).toFixed(2)}`}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
            {form.vinculo.id && (
              <div className="mt-1 flex items-center justify-between p-2 bg-muted/40 rounded text-sm">
                <span className="flex items-center gap-2"><Link2 className="h-3 w-3" />{form.vinculo.label}</span>
                <button type="button" onClick={() => setForm({ ...form, vinculo: { ...form.vinculo, id: null, label: null } })} className="text-muted-foreground hover:text-foreground"><X className="h-3 w-3" /></button>
              </div>
            )}
          </div>

          <div>
            <Label className="text-xs">Repetição</Label>
            <Select value={form.recorrencia.tipo} onValueChange={(v) => setForm({ ...form, recorrencia: { ...form.recorrencia, tipo: v } })}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>{REPETICOES.map(r => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}</SelectContent>
            </Select>
            {form.recorrencia.tipo === 'personalizada' && (
              <div className="grid grid-cols-2 gap-2 mt-2">
                <Input type="number" placeholder="A cada X dias" value={form.recorrencia.dias || ''} onChange={(e) => setForm({ ...form, recorrencia: { ...form.recorrencia, dias: parseInt(e.target.value) || 0 } })} />
                <Input type="date" placeholder="Data fim" value={form.recorrencia.data_fim || ''} onChange={(e) => setForm({ ...form, recorrencia: { ...form.recorrencia, data_fim: e.target.value } })} />
              </div>
            )}
          </div>

          <div>
            <Label className="text-xs">Notificações antes</Label>
            <div className="flex flex-wrap gap-1 mt-1">
              {NOTIF_OPTIONS.map(opt => {
                const active = form.notificacoes.find(n => n.tipo === 'antes' && n.minutos_antes === opt.min);
                return (
                  <button key={opt.min} type="button" onClick={() => toggleNotificacao(opt.min)}
                    className={`text-xs px-2 py-1 rounded border ${active ? 'bg-primary text-primary-foreground' : 'hover:bg-muted'}`}>
                    {opt.label}
                  </button>
                );
              })}
            </div>
          </div>

          <div>
            <Label className="text-xs">Observações</Label>
            <textarea className="w-full border rounded-md px-3 py-2 text-sm bg-background min-h-[60px]" value={form.observacoes} onChange={(e) => setForm({ ...form, observacoes: e.target.value })} />
          </div>
        </div>
        <div className="flex justify-end gap-2 px-4 py-3 border-t bg-muted/20 shrink-0">
          <Button variant="outline" onClick={onClose}>Cancelar</Button>
          <Button onClick={salvar} disabled={saving} data-testid="btn-salvar-lembrete">{saving ? 'Salvando...' : 'Salvar'}</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ============ Calendário simples ============
function CalendarioLembretes({ reminders, categoriasById, month, setMonth, onDetail }) {
  const year = month.getFullYear();
  const m = month.getMonth();
  const primeiroDia = new Date(year, m, 1);
  const ultimoDia = new Date(year, m + 1, 0);
  const startWeek = primeiroDia.getDay();
  const totalDays = ultimoDia.getDate();
  const nomeMes = month.toLocaleDateString('pt-BR', { month: 'long', year: 'numeric' });

  const byDate = useMemo(() => {
    const map = {};
    reminders.forEach(r => {
      const d = (r.data || '').split('T')[0];
      if (!d) return;
      if (!map[d]) map[d] = [];
      map[d].push(r);
    });
    return map;
  }, [reminders]);

  const cells = [];
  for (let i = 0; i < startWeek; i++) cells.push(null);
  for (let d = 1; d <= totalDays; d++) cells.push(d);

  const prev = () => setMonth(new Date(year, m - 1, 1));
  const next = () => setMonth(new Date(year, m + 1, 1));

  return (
    <Card>
      <CardContent className="pt-4">
        <div className="flex items-center justify-between mb-3">
          <Button variant="ghost" size="icon" onClick={prev}><ChevronLeft className="h-4 w-4" /></Button>
          <h3 className="font-semibold capitalize">{nomeMes}</h3>
          <Button variant="ghost" size="icon" onClick={next}><ChevronRight className="h-4 w-4" /></Button>
        </div>
        <div className="grid grid-cols-7 gap-1 text-xs">
          {['D', 'S', 'T', 'Q', 'Q', 'S', 'S'].map((d, i) => (
            <div key={i} className="text-center font-semibold text-muted-foreground">{d}</div>
          ))}
          {cells.map((d, i) => {
            const key = d ? `${year}-${String(m + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}` : null;
            const items = key ? (byDate[key] || []) : [];
            const isToday = key === new Date().toISOString().split('T')[0];
            return (
              <div key={i} className={`min-h-[70px] border rounded p-1 ${d ? '' : 'opacity-30'} ${isToday ? 'ring-2 ring-primary' : ''}`}>
                {d && <div className="text-xs font-semibold">{d}</div>}
                {items.slice(0, 3).map(r => {
                  const prio = PRIORIDADES.find(p => p.value === r.prioridade) || PRIORIDADES[1];
                  return (
                    <button key={r.id} onClick={() => onDetail(r)} className={`w-full text-left mt-0.5 px-1 rounded text-[10px] truncate text-white ${prio.cor}`}>
                      {r.titulo}
                    </button>
                  );
                })}
                {items.length > 3 && <div className="text-[10px] text-muted-foreground">+{items.length - 3}</div>}
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
}
